"use client";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRegisterSW } from "virtual:pwa-register/react";

/**
 * "A new version is available · Refresh" prompt.
 *
 * WHY THIS EXISTS
 * The service worker precaches the app shell. Before the accompanying
 * `skipWaiting`/`clientsClaim` fix a freshly installed SW sat in `waiting`
 * until every tab for the origin closed — and on a QR-scanned restaurant
 * storefront a phone can hold one tab open for days. A tester's device
 * therefore kept calling an API path that had been re-pathed days earlier,
 * producing 401s that read like a backend outage.
 *
 * `skipWaiting` + `clientsClaim` make the new SW activate and claim the
 * open clients immediately, but claiming a client does NOT re-execute the
 * page: the tab keeps running the OLD JS it already parsed. Only a
 * navigation picks up the new bundle. This component is that missing half.
 *
 * WHY IT DOES NOT RELOAD BY ITSELF
 * The refresh is deliberately user-initiated. A diner may be mid-payment;
 * yanking the page out from under them is strictly worse than a stale tab.
 *
 * WHY `onNeedReload` AND NOT ONLY `onNeedRefresh`
 * With `registerType: "autoUpdate"` the generated register module takes its
 * "auto" branch: it listens for the SW `activated` event and, if no
 * `onNeedReload` callback is supplied, calls `window.location.reload()`
 * outright — exactly the forced reload we must avoid. Supplying
 * `onNeedReload` intercepts that and turns it into this prompt.
 * `needRefresh` (fed by `onNeedRefresh`) is the equivalent signal in the
 * "prompt" branch; we honour both so the component keeps working if
 * `registerType` is ever changed.
 */
export function PwaUpdatePrompt() {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  // Set from the autoUpdate ("activated") path — see the note above.
  const [reloadNeeded, setReloadNeeded] = useState(false);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onNeedReload() {
      setReloadNeeded(true);
    },
  });

  const visible = (needRefresh || reloadNeeded) && !dismissed;
  if (!visible) return null;

  const refresh = async () => {
    // In the "prompt" branch this posts SKIP_WAITING; in the "autoUpdate"
    // branch the new SW is already active and this resolves to a no-op.
    // Neither branch reloads the page on its own here, so we do it — the
    // navigation is what actually swaps in the new bundle.
    try {
      await updateServiceWorker(true);
    } catch {
      /* activation failure shouldn't block the guest from reloading */
    }
    window.location.reload();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-auto fixed left-1/2 top-0 z-50 w-full max-w-[420px] -translate-x-1/2 px-3"
      style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}
    >
      {/* Dark-pill structure mirrors GroupBanner / TableBillBanner so the
          three transient banners read as one family. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 12px",
          background: "var(--ink-1)",
          color: "#fff",
          borderRadius: 14,
          boxShadow: "0 6px 20px -8px rgba(0,0,0,0.3)",
        }}
      >
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: "var(--primary-400, #34d399)",
            boxShadow: "0 0 0 4px rgba(52, 211, 153, 0.18)",
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700 }}>
            {t("v2.app_update.title", "A new version is available")}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,0.7)",
              marginTop: 1,
            }}
          >
            {t("v2.app_update.subtitle", "Refresh to get the latest menu.")}
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          style={{
            flexShrink: 0,
            border: "none",
            borderRadius: 10,
            padding: "6px 10px",
            fontSize: 12,
            fontWeight: 700,
            fontFamily: "inherit",
            cursor: "pointer",
            color: "#fff",
            background: "var(--primary-500, #0baa57)",
            whiteSpace: "nowrap",
          }}
        >
          {t("v2.app_update.refresh", "Refresh")}
        </button>
        <button
          type="button"
          aria-label={t("v2.app_update.dismiss", "Not now")}
          onClick={() => setDismissed(true)}
          style={{
            flexShrink: 0,
            width: 28,
            height: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            borderRadius: 999,
            background: "transparent",
            color: "rgba(255,255,255,0.7)",
            fontSize: 18,
            lineHeight: 1,
            fontFamily: "inherit",
            cursor: "pointer",
          }}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </div>
  );
}
